import { lazy, Suspense, useEffect, useState, type ComponentType } from 'react';
import { AppProvider } from './lib/data/store';
import { NavProvider, useNav } from './lib/nav';
import { AuthGate } from './components/AuthGate';
import { StatusStrip } from './components/StatusStrip';
import { TalkingPointsBanner } from './components/TalkingPoints';
import { TabBar } from './components/TabBar';
// AtTheDoor (the heart) + FirstRun stay eager so the door form and first paint are
// instant. The rest are code-split — notably MapView, which is the ONLY screen that
// pulls in Leaflet (~the bulk of the bundle), so the login/shell paints without it.
import { AtTheDoor } from './screens/AtTheDoor';
import { FirstRun } from './screens/FirstRun';
import { setupPwa } from './lib/pwa';

const lazyScreen = (load: () => Promise<Record<string, unknown>>, name: string) =>
  lazy(() => load().then((m) => ({ default: m[name] as ComponentType })));

const MapView = lazyScreen(() => import('./screens/MapView'), 'MapView');
const Coverage = lazyScreen(() => import('./screens/Coverage'), 'Coverage');
const Search = lazyScreen(() => import('./screens/Search'), 'Search');
const Coordinator = lazyScreen(() => import('./screens/Coordinator'), 'Coordinator');
const AdminPanel = lazyScreen(() => import('./screens/AdminPanel'), 'AdminPanel');
const Profile = lazyScreen(() => import('./screens/Profile'), 'Profile');
const Help = lazyScreen(() => import('./screens/Help'), 'Help');

const TUT_KEY = 'vc-tutorial-done';

function Screen() {
  const { tab } = useNav();
  switch (tab) {
    case 'coverage':
      return <Coverage />;
    case 'search':
      return <Search />;
    case 'door':
      return <AtTheDoor />;
    case 'coord':
      return <Coordinator />;
    case 'admin':
      return <AdminPanel />;
    case 'profile':
      return <Profile />;
    case 'help':
      return <Help />;
    case 'map':
    default:
      return <MapView />;
  }
}

function Shell() {
  return (
    <div className="shell">
      <StatusStrip />
      <TalkingPointsBanner />
      <main className="shell-body">
        <Suspense fallback={<div className="muted" style={{ padding: 24, textAlign: 'center' }}>Loading…</div>}>
          <Screen />
        </Suspense>
      </main>
      <TabBar />
    </div>
  );
}

function Authed() {
  const [tutDone, setTutDone] = useState(() => localStorage.getItem(TUT_KEY) === '1');
  if (!tutDone) {
    return (
      <FirstRun
        onDone={() => {
          localStorage.setItem(TUT_KEY, '1');
          setTutDone(true);
        }}
      />
    );
  }
  return <Shell />;
}

export function App() {
  useEffect(() => {
    // Restore accessibility + theme preferences, then register the service worker.
    if (localStorage.getItem('vc-large-target') === '1') document.body.classList.add('large-target');
    if (localStorage.getItem('vc-theme') === 'light') document.documentElement.setAttribute('data-theme', 'light');
    setupPwa(() => {});
  }, []);

  return (
    <AppProvider>
      <NavProvider initial="map">
        <AuthGate>{() => <Authed />}</AuthGate>
      </NavProvider>
    </AppProvider>
  );
}
